import { useDeferredValue, useEffect, useId, useMemo, useState } from 'react'
import { Plus, ChevronRight } from 'lucide-react'
import { isPersonMatchTerm, type TPersonMatch, type TShowRoleAssignment } from '@schemas'
import { useShowStaffPeopleSearch } from '@/api/hooks'
import { subscribeToPersonAccessDenials } from '@/lib/apiClient'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

export type StaffRole = TShowRoleAssignment['role']

export const ALL_STAFF_ROLES: StaffRole[] = [
  'organizer',
  'presiding_judge',
  'lead_secretary',
  'secretary',
  'steward',
  'judge',
  'viewer',
]

export const ROLE_CONFIG: Record<
  StaffRole,
  { title: string; helper: string; minimum?: number; hidden?: boolean }
> = {
  organizer: {
    title: 'Organizers',
    helper: 'Organizers can update show details and finalize the show. Assign at least one.',
    minimum: 1,
  },
  presiding_judge: {
    title: 'Presiding Judges',
    helper: 'Presiding Judges can manage judging, finalize awards, and view result reports.',
  },
  secretary: {
    title: 'Secretaries',
    helper: 'Secretaries record results and manage intake. Assign at least one.',
    minimum: 1,
  },
  lead_secretary: {
    title: 'Lead Secretaries',
    helper:
      'Lead Secretaries can update show status, finalize awards, and manage staff. Assign when delegating intake.',
  },
  steward: {
    title: 'Stewards',
    helper: 'Stewards assist judges and manage logistics.',
  },
  judge: {
    title: 'Judges',
    helper: 'Assign at least one judge who will evaluate entries.',
    minimum: 1,
  },
  viewer: {
    title: 'Viewers',
    helper: 'Viewers have read-only access; optional to track here.',
    hidden: true,
  },
}

export const VISIBLE_ROLES = ALL_STAFF_ROLES.filter(role => !ROLE_CONFIG[role].hidden)

export type StaffInput = { personId?: string; matchProof?: string; name?: string; contact?: string }
type Props = {
  showId: string

  assignments: TShowRoleAssignment[]
  canManage: boolean
  permissionNotice?: string
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  isAdding: boolean
  isRemoving: boolean
  onAdd: (role: StaffRole, input: StaffInput) => Promise<boolean>
  onRemove: (assignmentId: string) => Promise<boolean>
  /** Administrators only: invite a staff person who has no account to sign in. */
  onInvite?: (person: { id: string; name: string; email?: string | null }) => void
}

const SIGN_IN_LABELS: Record<
  NonNullable<TShowRoleAssignment['signInStatus']>,
  { label: string; variant: 'secondary' | 'outline' | 'destructive' }
> = {
  active: { label: 'Can sign in', variant: 'secondary' },
  invited: { label: 'Invited to sign in', variant: 'outline' },
  disabled: { label: 'Sign-in disabled', variant: 'destructive' },
  none: { label: 'No sign-in', variant: 'outline' },
}

function staffName(assignment: TShowRoleAssignment) {
  return assignment.person?.name ?? assignment.name ?? 'Unnamed staff member'
}
function roleName(role: StaffRole) {
  return ROLE_CONFIG[role].title.replace(/ies$/, 'y').replace(/s$/, '')
}

export function ShowStaffRoster({
  showId,
  assignments,
  canManage,
  permissionNotice,
  isLoading,
  isError,
  onRetry,
  isAdding,
  isRemoving,
  onAdd,
  onRemove,
  onInvite,
}: Props) {
  const [roleFilter, setRoleFilter] = useState<StaffRole | 'all'>('all')
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<TShowRoleAssignment | null>(null)
  const filterId = useId()
  const disabled = !canManage || isLoading || isAdding || isRemoving
  const roles = roleFilter === 'all' ? VISIBLE_ROLES : [roleFilter]

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold tracking-tight">Staff</h2>
          <p className="text-sm text-muted-foreground">
            Assign people to show roles. Each addition or removal saves separately from show
            settings.
          </p>
          <p className="text-sm text-muted-foreground">
            A staff member uses these roles once they can sign in.{' '}
            {onInvite
              ? 'Invite anyone marked No sign-in.'
              : 'Ask an administrator to invite anyone marked No sign-in.'}
          </p>
        </div>
        <Dialog
          open={adding}
          onOpenChange={open => {
            if (!isAdding) setAdding(open)
          }}
        >
          <DialogTrigger asChild>
            <Button disabled={disabled} className="shrink-0 self-start">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add staff
            </Button>
          </DialogTrigger>
          {adding && (
            <AddStaffDialog
              showId={showId}
              assignments={assignments}
              initialRole={roleFilter === 'all' ? '' : roleFilter}
              pending={isAdding}
              canManage={canManage}
              onAdd={onAdd}
              onClose={() => setAdding(false)}
            />
          )}
        </Dialog>
      </div>
      {permissionNotice && <p className="text-sm text-muted-foreground">{permissionNotice}</p>}
      {isError ? (
        <div role="alert" className="space-y-2 rounded-md border p-4">
          <p>Unable to refresh staff assignments. Any displayed assignments may be out of date.</p>
          <Button variant="outline" onClick={onRetry}>
            Retry
          </Button>
        </div>
      ) : null}
      <div className="max-w-sm space-y-2">
        <Label htmlFor={filterId}>Filter by role</Label>
        <Select
          value={roleFilter}
          onValueChange={value => setRoleFilter(value as StaffRole | 'all')}
        >
          <SelectTrigger id={filterId}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {VISIBLE_ROLES.map(role => (
              <SelectItem key={role} value={role}>
                {ROLE_CONFIG[role].title} ({assignments.filter(item => item.role === role).length})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <div className="divide-y border-y">
          {roles.map(role => {
            const people = assignments
              .filter(item => item.role === role)
              .sort((a, b) => staffName(a).localeCompare(staffName(b)))
            return (
              <details
                key={`${roleFilter}-${role}`}
                open={roleFilter !== 'all'}
                className="group py-1"
              >
                <summary className="flex min-h-12 cursor-pointer list-none items-center gap-3 rounded-sm py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
                  <ChevronRight
                    className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90"
                    aria-hidden="true"
                  />
                  <h3 className="flex-1 font-medium">{ROLE_CONFIG[role].title}</h3>
                  <Badge variant="secondary">{people.length}</Badge>
                </summary>
                <div className="space-y-3 pb-4 pl-7">
                  <p className="text-sm text-muted-foreground">{ROLE_CONFIG[role].helper}</p>
                  {people.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No {ROLE_CONFIG[role].title.toLowerCase()} assigned yet.
                    </p>
                  ) : (
                    <ul className="divide-y">
                      {people.map(assignment => (
                        <li
                          key={assignment.id}
                          className="flex items-start justify-between gap-3 py-3"
                        >
                          <div className="min-w-0 space-y-1">
                            <p className="break-words text-sm font-medium">
                              {staffName(assignment)}
                            </p>
                            {assignment.person?.credentialNumber && (
                              <p className="text-xs text-muted-foreground">
                                Credential number: {assignment.person.credentialNumber}
                              </p>
                            )}
                            <div className="flex flex-wrap gap-1">
                              {assignment.person?.ahstcMember && (
                                <Badge variant="outline">AHSTC Member</Badge>
                              )}
                              {assignment.person && assignment.signInStatus ? (
                                <Badge variant={SIGN_IN_LABELS[assignment.signInStatus].variant}>
                                  {SIGN_IN_LABELS[assignment.signInStatus].label}
                                </Badge>
                              ) : null}
                            </div>
                            <p className="break-words text-sm text-muted-foreground">
                              {assignment.person?.email ??
                                assignment.person?.phone ??
                                assignment.contact ??
                                'No contact provided'}
                            </p>
                          </div>
                          <div className="flex shrink-0 flex-col items-end gap-1">
                            {onInvite &&
                            assignment.person?.id &&
                            assignment.signInStatus === 'none' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  onInvite({
                                    id: assignment.person!.id!,
                                    name: assignment.person!.name,
                                    email: assignment.person!.email,
                                  })
                                }
                              >
                                Invite to sign in
                              </Button>
                            ) : null}
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={disabled || !assignment.id}
                              onClick={() => setRemoving(assignment)}
                              aria-label={`Remove ${staffName(assignment)} as ${roleName(role)}`}
                            >
                              Remove
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </details>
            )
          })}
        </div>
      )}
      <Dialog
        open={Boolean(removing)}
        onOpenChange={open => {
          if (!open && !isRemoving) setRemoving(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove staff assignment?</DialogTitle>
            <DialogDescription>
              {removing
                ? `Remove ${staffName(removing)} as ${roleName(removing.role)}? Their person record and other role assignments will remain.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6">
            <Button variant="outline" disabled={isRemoving} onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={disabled || !removing?.id}
              onClick={async () => {
                if (removing?.id && (await onRemove(removing.id))) setRemoving(null)
              }}
            >
              {isRemoving ? 'Removing…' : 'Remove assignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function AddStaffDialog({
  showId,
  assignments,
  initialRole,
  pending,
  canManage,
  onAdd,
  onClose,
}: {
  showId: string
  assignments: TShowRoleAssignment[]
  initialRole: StaffRole | ''
  pending: boolean
  canManage: boolean
  onAdd: Props['onAdd']
  onClose: () => void
}) {
  const [role, setRole] = useState<StaffRole | ''>(initialRole)
  const [mode, setMode] = useState<'search' | 'custom'>('search')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<TPersonMatch | null>(null)
  const [name, setName] = useState('')
  const [contact, setContact] = useState('')
  const [error, setError] = useState('')
  const [isOnline, setIsOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const update = () => setIsOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])
  useEffect(() => {
    if (!isOnline) setSelected(null)
  }, [isOnline])
  const id = useId()
  const deferredSearch = useDeferredValue(search.trim())
  const query = useShowStaffPeopleSearch(
    showId,
    deferredSearch,
    canManage && isOnline && mode === 'search'
  )
  useEffect(() => {
    setSelected(null)
    setSearch('')
  }, [showId])
  useEffect(
    () =>
      subscribeToPersonAccessDenials(lookup => {
        setSelected(null)
        // A refused search keeps its text so the error below explains it.
        if (!lookup) setSearch('')
      }),
    []
  )
  // A refreshed search reissues match proofs, so submit the chosen person's current one.
  const selectedMatch = query.data?.people.find(person => person.id === selected?.id) ?? selected
  const existingIds = useMemo(
    () =>
      new Set(
        assignments
          .filter(item => item.role === role)
          .map(item => item.personId)
          .filter(Boolean)
      ),
    [assignments, role]
  )
  const results = useMemo(
    () =>
      [...(query.data?.people ?? [])]
        .filter(person => person.id && !existingIds.has(person.id))
        .sort(
          (a, b) =>
            Number(Boolean(b.ahstcMember)) - Number(Boolean(a.ahstcMember)) ||
            (a.name ?? '').localeCompare(b.name ?? '')
        ),
    [query.data, existingIds]
  )
  const duplicate = Boolean(selected?.id && existingIds.has(selected.id))
  const validPerson = mode === 'custom' ? Boolean(name.trim()) : Boolean(selected?.id && !duplicate)
  const disabled = pending || !canManage || !isOnline

  return (
    <DialogContent contentClassName="max-h-[calc(100dvh-2rem)] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Add staff</DialogTitle>
        <DialogDescription>
          Choose a role and a person. A person can hold more than one role.
        </DialogDescription>
      </DialogHeader>
      <form
        className="mt-6 space-y-5"
        onSubmit={async event => {
          event.preventDefault()
          if (disabled || !role || !validPerson) return
          setError('')
          const input =
            mode === 'custom'
              ? { name: name.trim(), contact: contact.trim() || undefined }
              : { personId: selected!.id!, matchProof: selectedMatch!.matchProof }
          if (await onAdd(role, input)) onClose()
          else setError('Unable to add staff. Your details are still here; try again.')
        }}
      >
        {!isOnline && <p role="alert">Connect to match people and assign staff.</p>}
        <div className="space-y-2">
          <Label htmlFor={`${id}-role`}>Role</Label>
          <Select
            value={role}
            onValueChange={value => {
              setRole(value as StaffRole)
              setError('')
            }}
            disabled={disabled}
          >
            <SelectTrigger
              id={`${id}-role`}
              aria-describedby={role ? `${id}-role-help` : undefined}
            >
              <SelectValue placeholder="Choose a role" />
            </SelectTrigger>
            <SelectContent>
              {VISIBLE_ROLES.map(value => (
                <SelectItem key={value} value={value}>
                  {roleName(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {role && (
            <p id={`${id}-role-help`} className="text-sm text-muted-foreground">
              {ROLE_CONFIG[role].helper}
            </p>
          )}
        </div>
        {mode === 'search' ? (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor={`${id}-search`}>Search People</Label>
              <Input
                id={`${id}-search`}
                value={search}
                onChange={event => {
                  setSearch(event.target.value)
                  setSelected(null)
                  setError('')
                }}
                placeholder="Name, email, or phone"
                disabled={disabled}
                aria-describedby={`${id}-search-help`}
              />
            </div>
            <p id={`${id}-search-help`} className="text-sm text-muted-foreground">
              Type at least three letters or digits. People already assigned this role are excluded.
            </p>
            {isOnline &&
              isPersonMatchTerm(deferredSearch) &&
              (query.isError ? (
                <p role="alert" className="text-sm text-destructive">
                  Unable to search people.{' '}
                  <Button type="button" variant="link" onClick={() => void query.refetch()}>
                    Retry
                  </Button>
                </p>
              ) : query.isFetching || deferredSearch !== search.trim() ? (
                <p role="status" className="text-sm text-muted-foreground">
                  Searching…
                </p>
              ) : results.length ? (
                <ul
                  aria-label="Matching people"
                  className="max-h-52 divide-y overflow-y-auto rounded-md border"
                >
                  {results.map(person => (
                    <li key={person.id} className="flex items-start justify-between gap-3 p-3">
                      <div className="min-w-0 space-y-1">
                        <p className="break-words text-sm font-medium">{person.name}</p>
                        {person.credentialNumber && (
                          <p className="text-xs text-muted-foreground">
                            Credential number: {person.credentialNumber}
                          </p>
                        )}
                        <p className="break-words text-xs text-muted-foreground">
                          {[person.displayCity, person.displayState].filter(Boolean).join(', ')}
                        </p>
                        <p className="break-words text-xs text-muted-foreground">
                          {[person.email, person.phone].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant={selected?.id === person.id ? 'secondary' : 'outline'}
                        aria-pressed={selected?.id === person.id}
                        aria-label={`Select ${person.name}`}
                        onClick={() => {
                          setSelected(person)
                          setError('')
                        }}
                        disabled={disabled}
                        className="shrink-0"
                      >
                        {selected?.id === person.id ? 'Selected' : 'Select'}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No matching people available for this role.
                </p>
              ))}
            {query.data?.refineSearch && (
              <p className="text-sm text-muted-foreground">
                More people match. Refine your search to find the right person.
              </p>
            )}
            {duplicate && (
              <p role="alert" className="text-sm text-destructive">
                This person already has the selected role. Choose a different role or person.
              </p>
            )}
            <Button
              type="button"
              variant="link"
              className="h-auto px-0"
              onClick={() => {
                setMode('custom')
                setError('')
              }}
              disabled={disabled}
            >
              Use a custom contact
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor={`${id}-name`}>Name</Label>
              <Input
                id={`${id}-name`}
                value={name}
                onChange={event => setName(event.target.value)}
                required
                disabled={disabled}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${id}-contact`}>Contact details (optional)</Label>
              <Input
                id={`${id}-contact`}
                value={contact}
                onChange={event => setContact(event.target.value)}
                placeholder="Email or phone"
                disabled={disabled}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Custom contacts are not linked to a People record. Select an existing person when
              available so their show roles and exhibiting eligibility can be matched.
            </p>
            <Button
              type="button"
              variant="link"
              className="h-auto px-0"
              onClick={() => {
                setMode('search')
                setError('')
              }}
              disabled={disabled}
            >
              Search existing people
            </Button>
          </div>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={disabled || !role || !validPerson}>
            {pending ? 'Adding…' : 'Add staff'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
